import type { KgInsight, KgInsightSection as KgInsightSectionData } from '@api';
import { KeyStats } from '@design/components/key-stats/KeyStats';
import { CohortTable } from '@terminal/modules/reflexivity-graph/components/cohort-table/CohortTable';
import { GeoExposureCards } from '@terminal/modules/reflexivity-graph/components/geo-exposure-cards/GeoExposureCards';
import { ThemeCard } from '@terminal/modules/reflexivity-graph/components/theme-card/ThemeCard';
import { ReflexivityGraphLayout } from '@terminal/modules/reflexivity-graph/ReflexivityGraphLayout';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { KgInsightSection } from '@terminal/views/kg-insight/components/kg-insight-section/KgInsightSection';
import { LeadershipQuotes } from '@terminal/views/kg-insight/components/leadership-quotes/LeadershipQuotes';
import { detailsGridToKeyStats } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/components/kg-insight-content/utils/kg-insight-content-utils';
import { TopTakeaways } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/components/top-takeaways/TopTakeaways';
import { RefCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './KgInsightSectionRenderer.module.scss';

export interface KgInsightSectionRendererProps {
  section: KgInsightSectionData;
  kgInsight: KgInsight;
  setSectionRef: (id: string) => RefCallback<HTMLElement>;
}

export const KgInsightSectionRenderer = ({
  section,
  kgInsight,
  setSectionRef,
}: KgInsightSectionRendererProps) => {
  const { t } = useTranslation(['insights']);

  switch (section.type) {
    case 'takeaway':
      return (
        <TopTakeaways
          kgInsight={kgInsight}
          source={POSTHOG_TRACKING_SOURCES.KG_INSIGHT_PAGE}
          ref={setSectionRef('takeaway')}
        />
      );
    case 'quotes':
      return (
        <KgInsightSection
          ref={setSectionRef('quotes')}
          title={t('insights:catalystGeneration.section_quotes')}
          subtitle={t('insights:catalystGeneration.leadershipQuotes.subtitle')}
        >
          <LeadershipQuotes kgInsight={kgInsight} />
        </KgInsightSection>
      );
    case 'details_grid':
      return (
        <KgInsightSection
          ref={setSectionRef('details_grid')}
          title={t(
            `insights:catalystGeneration.details_grid.${kgInsight.category}.title`
          )}
          subtitle={t(
            `insights:catalystGeneration.details_grid.${kgInsight.category}.subtitle`
          )}
        >
          <KeyStats items={detailsGridToKeyStats(section)} />
        </KgInsightSection>
      );
    case 'graph':
      return (
        <KgInsightSection
          ref={setSectionRef('graph')}
          title={t('insights:catalystGeneration.reflexivityGraph.title')}
          subtitle={t('insights:catalystGeneration.reflexivityGraph.subtitle')}
          cardsClassName={styles.graphCards}
        >
          {section.data.cohorts.map(cohort => (
            <ThemeCard
              key={cohort.data.theme_id}
              cohort={cohort}
              cohortReadTitle={t(
                'insights:catalystGeneration.reflexivityGraph.cohortRead'
              )}
              askAlfredSuggestions={cohort.data.alfred_ctas}
            >
              <CohortTable
                columns={cohort.data.table.columns}
                rows={cohort.data.table.rows}
                catalystPublicationDate={kgInsight.created_at}
              />
            </ThemeCard>
          ))}
        </KgInsightSection>
      );
    case 'geo':
      return (
        <KgInsightSection ref={setSectionRef('geo')}>
          <ReflexivityGraphLayout
            color="gold"
            headerNode={
              <div className={styles.geoExposureHeader}>
                <p className={styles.geoExposureTitle}>
                  {t('insights:catalystGeneration.section_geo')}
                </p>
                <p className={styles.geoExposureDescription}>
                  {t('insights:catalystGeneration.geoExposureDescription')}
                </p>
              </div>
            }
            description={section.data.subtitle}
            cohortReadTitle={t(
              'insights:catalystGeneration.reflexivityGraph.geoRead'
            )}
            cohortRead={section.data.read}
            askAlfredSuggestions={section.data.alfred_ctas}
          >
            <GeoExposureCards cards={section.data.cards} />
          </ReflexivityGraphLayout>
        </KgInsightSection>
      );
    default:
      return null;
  }
};
